import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, Observable, of, timer } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { ConfigService } from '../../core/services/config.service';
import { OAuthUrlService } from './oauth-url.service';
import { SecureStorageService } from '../../core/services/secure-storage/secure-storage.service';
import { environment } from '../../../environments/environment';

interface SessionUser {
  id: string;
  name: string | null;
  lastName: string | null;
  email: string;
  provider?: string | null;
  enabled: boolean;
  rol: {
    id: string;
    name: string;
  } | null;
  tenant: {
    id: string;
    tenant: string;
  } | null;
  tenantId: string | null;
}

interface SessionData {
  user: SessionUser;
  sessionToken: string;
  expires: string;
}

interface AuthResponse {
  sessionToken: string;
  user: {
    id: string;
    email: string;
    name: string | null;
    role: {
      id: string;
      name: string;
    } | null;
    enabled: boolean;
  };
  expiresAt: string;
}

export type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

@Injectable({
  providedIn: 'root'
})
export class SessionAuthService {
  private readonly API_BASE_URL = environment.microservices.msconfiguracionaplicacion;
  private readonly SESSION_STORAGE_KEY = 'session-data';

  private sessionSubject = new BehaviorSubject<SessionData | null>(null);
  private statusSubject = new BehaviorSubject<AuthStatus>('loading');
  private isValidating = false;

  public session$ = this.sessionSubject.asObservable();
  public status$ = this.statusSubject.asObservable();

  constructor(
    private http: HttpClient,
    private router: Router,
    private configService: ConfigService,
    private oauthUrlService: OAuthUrlService,
    private secureStorage: SecureStorageService
  ) {
    this.initializeSession();
    this.setupBeforeUnloadCleanup();
    this.startSessionRefresh();
  }

  private initializeSession(): void {
    const storedSession = localStorage.getItem(this.SESSION_STORAGE_KEY);

    if (storedSession) {
      try {
        const sessionData: SessionData = JSON.parse(storedSession);
        const now = new Date();
        const expires = new Date(sessionData.expires);

        if (expires > now) {
          this.sessionSubject.next(sessionData);
          this.statusSubject.next('authenticated');
        } else {
          this.clearSession();
        }
      } catch (error) {
        console.error('Error parsing stored session:', error);
        this.clearSession();
      }
    } else {
      this.statusSubject.next('unauthenticated');
    }
  }

  private startSessionRefresh(): void {
    timer(0, 5 * 60 * 1000).pipe(
      switchMap(() => {
        const currentSession = this.sessionSubject.value;
        if (currentSession) {
          return this.validateSession();
        }
        return of(null);
      })
    ).subscribe();
  }

  signIn(provider: 'cognito' | 'prevalent'): void {
    this.redirectToPortalAliados();
  }

  createSessionFromJWT(jwtToken: string): Observable<boolean> {
    this.statusSubject.next('loading');

    return this.http.post<AuthResponse>(`${this.API_BASE_URL}api/AuthSession/create-session`, {
      jwtToken: jwtToken
    }).pipe(
      tap((response) => {
        const sessionData: SessionData = {
          user: {
            id: response.user.id,
            name: response.user.name,
            lastName: null,
            email: response.user.email,
            provider: 'cognito',
            enabled: response.user.enabled,
            rol: response.user.role ? {
              id: response.user.role.id,
              name: response.user.role.name
            } : null,
            tenant: null,
            tenantId: null
          },
          sessionToken: response.sessionToken,
          expires: response.expiresAt
        };

        this.sessionSubject.next(sessionData);
        this.statusSubject.next('authenticated');
        localStorage.setItem(this.SESSION_STORAGE_KEY, JSON.stringify(sessionData));
      }),
      map(() => true),
      catchError((error: HttpErrorResponse) => {
        console.error('Error creating session:', error);
        this.statusSubject.next('unauthenticated');
        return of(false);
      })
    );
  }

  async handleOAuthCallback(code: string, state: string): Promise<void> {
    this.statusSubject.next('loading');

    try {
      const codeVerifier = localStorage.getItem('oauth_code_verifier');

      if (!codeVerifier) {
        throw new Error('Code verifier not found. Possible security issue.');
      }

      localStorage.removeItem('oauth_code_verifier');
      localStorage.removeItem('oauth_state');

      const response = await this.http.post<AuthResponse>(`${this.API_BASE_URL}api/AuthSession/create-session`, {
        code: code,
        codeVerifier: codeVerifier,
        state: state
      }).toPromise();

      if (!response) {
        throw new Error('No response received from server');
      }

      const sessionData: SessionData = {
        user: {
          id: response.user.id,
          name: response.user.name,
          lastName: null,
          email: response.user.email,
          provider: 'cognito',
          enabled: response.user.enabled,
          rol: response.user.role ? {
            id: response.user.role.id,
            name: response.user.role.name
          } : null,
          tenant: null,
          tenantId: null
        },
        sessionToken: response.sessionToken,
        expires: response.expiresAt
      };

      this.sessionSubject.next(sessionData);
      this.statusSubject.next('authenticated');
      localStorage.setItem(this.SESSION_STORAGE_KEY, JSON.stringify(sessionData));

      sessionStorage.removeItem('oauth_code_verifier');
      sessionStorage.removeItem('oauth_state');

    } catch (error) {
      console.error('OAuth callback error:', error);
      this.statusSubject.next('unauthenticated');
      this.clearSession();

      throw error;
    }
  }

  validateSession(): Observable<boolean> {
    const currentSession = this.sessionSubject.value;

    if (this.isValidating) {
      return of(this.statusSubject.value === 'authenticated');
    }

    if (!currentSession) {
      if (this.statusSubject.value !== 'unauthenticated') {
        this.statusSubject.next('unauthenticated');
      }
      return of(false);
    }

    this.isValidating = true;

    return this.http.get<any>(`${this.API_BASE_URL}api/AuthSession/validate-session`, {
      headers: {
        'auth': currentSession.sessionToken
      }
    }).pipe(
      tap((response) => {
        if (response && response.email) {
          const updatedSession: SessionData = {
            ...currentSession,
            user: {
              id: response.id,
              name: response.name,
              lastName: null,
              email: response.email,
              provider: currentSession.user.provider,
              enabled: response.enabled,
              rol: response.role ? {
                id: response.role.id,
                name: response.role.name
              } : null,
              tenant: null,
              tenantId: null
            }
          };
          this.sessionSubject.next(updatedSession);
          this.statusSubject.next('authenticated');
          localStorage.setItem(this.SESSION_STORAGE_KEY, JSON.stringify(updatedSession));
        } else {
          this.clearSession();
        }
      }),
      map((response) => {
        this.isValidating = false;
        return !!response && !!response.email;
      }),
      catchError((error: HttpErrorResponse) => {
        console.error('Error validating session:', error);
        this.isValidating = false;
        this.clearSession();
        return of(false);
      })
    );
  }

  signOut(): Observable<void> {
    return this.http.post<void>(`${this.API_BASE_URL}api/AuthSession/logout`, {}).pipe(
      tap(() => {
        this.clearSession();
        const logoutUrl = environment.portalAliadosLogoutUrl;
        if (logoutUrl) {
          window.location.href = logoutUrl;
        } else {
          this.redirectToPortalAliados();
        }
      }),
      catchError((error) => {
        console.error('Error during logout:', error);
        this.clearSession();
        this.redirectToPortalAliados();
        return of(undefined);
      }),
      map(() => undefined)
    );
  }

  private async redirectToPortalAliados(): Promise<void> {
    try {
      const oauthUrl = await this.oauthUrlService.buildPortalAliadosOAuthUrl();
      window.location.href = oauthUrl;
    } catch (error) {
      console.error('Error redirecting to Portal Aliados:', error);
    }
  }

  private clearSession(): void {
    this.sessionSubject.next(null);
    this.statusSubject.next('unauthenticated');

    localStorage.removeItem(this.SESSION_STORAGE_KEY);
    localStorage.removeItem('oauth_code_verifier');
    localStorage.removeItem('oauth_state');
    sessionStorage.removeItem('oauth_code_verifier');
    sessionStorage.removeItem('oauth_state');

    try {
      this.secureStorage.removeItem('fidelPermissions');
      this.secureStorage.removeItem('idVid');
    } catch (error) {
      console.warn('Error clearing secure storage:', error);
    }

    localStorage.removeItem('access_token');
    localStorage.removeItem('id_token');
    localStorage.removeItem('refresh_token');
    sessionStorage.removeItem('access_token');
    sessionStorage.removeItem('id_token');
    sessionStorage.removeItem('refresh_token');
    localStorage.removeItem('amplify-auto-track-session');
    localStorage.removeItem('amplify-signin-with-hostedUI');

    this.clearApplicationSpecificData();
    this.isValidating = false;
  }

  private setupBeforeUnloadCleanup(): void {
    window.addEventListener('beforeunload', () => {
      sessionStorage.removeItem('oauth_code_verifier');
      sessionStorage.removeItem('oauth_state');
    });
  }

  private clearApplicationSpecificData(): void {
    const keysToRemove = [
      'userPreferences',
      'cacheData',
      'temporaryData'
    ];

    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    });
  }

  getSession(): SessionData | null {
    return this.sessionSubject.value;
  }

  getStatus(): AuthStatus {
    return this.statusSubject.value;
  }

  isAuthenticated(): boolean {
    return this.statusSubject.value === 'authenticated';
  }

  getSessionToken(): string | null {
    const session = this.sessionSubject.value;
    return session?.sessionToken || null;
  }

  getUser(): SessionUser | null {
    const session = this.sessionSubject.value;
    return session?.user || null;
  }

  getUserRole(): string | null {
    const user = this.getUser();
    return user?.rol?.name || null;
  }

  getTenantId(): string | null {
    const user = this.getUser();
    return user?.tenantId || null;
  }
}
